import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import { fetchPatients } from "../../redux/slices/patientSlice";
import { fetchDoctors } from "../../redux/slices/doctorSlice";
import { fetchProcedures } from "../../redux/slices/procedureSlice";
import {
  createAppointmentThunk,
  updateAppointmentThunk,
} from "../../redux/slices/appointmentSlice";

const STATUS_OPTIONS = [
  { value: "Booked", label: "Booked" },
  { value: "Completed", label: "Completed" },
  { value: "Cancelled", label: "Cancelled" },
  { value: "NoShow", label: "No Show" },
];

const STATUS_BADGE = {
  Booked: "primary",
  Completed: "success",
  Cancelled: "danger",
  NoShow: "warning",
};

const EMPTY = {
  appointmentDate: "",
  fromTime: "",
  toTime: "",
  patientId: "",
  patientName: "",
  doctorId: "",
  procedureId: "",
  status: "Booked",
  remarks: "",
};

const pad = (n) => String(n).padStart(2, "0");

const addMinutes = (time, mins) => {
  if (!time) return "";
  const [h, m] = time.split(":").map(Number);
  const total = h * 60 + m + mins;
  return `${pad(Math.floor(total / 60) % 24)}:${pad(total % 60)}`;
};

const fmt12 = (t) => {
  if (!t) return "—";
  const parts = t.split(":");
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${pad(m)} ${ampm}`;
};

const buildWhatsAppURL = (mobile, message) => {
  const clean = (mobile || "").replace(/\D/g, "");
  if (!clean) return null;
  const number = clean.startsWith("91") ? clean : `91${clean}`;
  return `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
};

const AppointmentModal = ({ show, onClose, editRecord, initialDate, initialTime }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const patients = useSelector((s) => s.patients.list);
  const doctors = useSelector((s) => s.doctors.list);
  const procedures = useSelector((s) => s.procedures.list);

  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const isEdit = !!(editRecord?.appointmentID || editRecord?.id);

  const patientOptions = patients.map((p) => ({
    value: p.patientID || p.id,
    label: `${p.patientName} — ${p.mobileNumber || p.mobile || ""} (OP: ${p.opNumber || "—"})`,
    patientName: p.patientName,
    mobile: p.mobileNumber || p.mobile || "",
  }));

  const doctorOptions = doctors.map((d) => ({
    value: d.doctorID || d.id,
    label: d.doctorName,
    availability: d.availabilitySchedule || "",
  }));

  const procedureOptions = procedures.map((p) => ({
    value: p.procedureID || p.id,
    label: p.procedureName,
    duration: p.defaultDuration || p.durationMinutes || 30,
  }));

  useEffect(() => {
    dispatch(fetchPatients());
    dispatch(fetchDoctors());
    dispatch(fetchProcedures());
  }, [dispatch]);

  useEffect(() => {
    if (editRecord) {
      setForm({ ...EMPTY, ...editRecord });
    } else {
      const today = new Date().toISOString().split("T")[0];
      setForm({ ...EMPTY, appointmentDate: initialDate || today, fromTime: initialTime || "" });
    }
  }, [editRecord, show, initialDate, initialTime]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleProcedureChange = (opt) => {
    setForm((f) => ({
      ...f,
      procedureId: opt?.value ?? "",
      toTime: opt ? addMinutes(f.fromTime, opt.duration) : f.toTime,
    }));
  };

  const handleFromTimeChange = (e) => {
    const t = e.target.value;
    const proc = procedureOptions.find((p) => p.value === form.procedureId);
    setForm((f) => ({ ...f, fromTime: t, toTime: proc ? addMinutes(t, proc.duration) : f.toTime }));
  };

  const promptWhatsApp = (booked, patientMobile) => {
    if (!patientMobile) return;
    const dateStr = new Date(booked.appointmentDate || form.appointmentDate).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
    const timeStr = fmt12(booked.fromTime || form.fromTime);
    const docName = booked.doctorName || doctors.find((d) => (d.doctorID || d.id) === (booked.doctorId || form.doctorId))?.doctorName || "";
    const message = `Hello! Your dental appointment has been confirmed.\n\n📅 Date: ${dateStr}\n⏰ Time: ${timeStr}${docName ? `\n👨‍⚕️ Doctor: Dr. ${docName}` : ""}\n\nPlease arrive 5 minutes early. Thank you! 🦷`;
    const url = buildWhatsAppURL(patientMobile, message);
    if (!url) return;
    Swal.fire({
      title: "Appointment Booked!",
      text: "Send a WhatsApp confirmation to the patient?",
      icon: "success",
      showCancelButton: true,
      confirmButtonText: "Send WhatsApp",
      cancelButtonText: "Skip",
      confirmButtonColor: "#25d366",
    }).then((result) => {
      if (result.isConfirmed) window.open(url, "_blank");
    });
  };

  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await dispatch(updateAppointmentThunk({ id: editRecord.appointmentID || editRecord.id, data: form })).unwrap();
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Status updated", showConfirmButton: false, timer: 2000 });
      onClose();
    } catch (err) {
      const msg = typeof err === "string" ? err : (err?.message || "Failed to update status.");
      Swal.fire("Error", msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleBook = async (e) => {
    e.preventDefault();
    if (!form.patientId || !form.appointmentDate || !form.fromTime || !form.doctorId) {
      Swal.fire("Required", "Patient, Doctor, Date and From Time are required.", "warning");
      return;
    }
    setSaving(true);
    try {
      const result = await dispatch(createAppointmentThunk(form)).unwrap();
      const patientOpt = patientOptions.find((o) => o.value === form.patientId);
      onClose();
      promptWhatsApp(result || form, patientOpt?.mobile || "");
    } catch (err) {
      const msg = typeof err === "string" ? err : (err?.message || "Failed to book appointment.");
      Swal.fire("Booking Failed", msg, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateInvoice = () => {
    const rec = editRecord || {};
    const appointmentData = {
      appointmentId: rec.appointmentID || rec.id,
      patientId: rec.patientID || rec.patientId,
      patientName: rec.patientName,
      doctorId: rec.doctorID || rec.doctorId,
      procedureId: rec.procedureID || rec.procedureId,
      procedureName: rec.procedureName,
    };
    onClose();
    navigate(
      `/invoices/new?appointmentId=${appointmentData.appointmentId}`,
      { state: { appointment: appointmentData } }
    );
  };

  const selectedDoc = doctorOptions.find((o) => o.value === form.doctorId);
  const selectedProc = procedureOptions.find((o) => o.value === (form.procedureId || form.procedureID));

  if (!show) return null;

  // Read-only field helpers
  const readVal = (label, value) => (
    <div className="col-lg-6 mb-2">
      <div className="text-muted small">{label}</div>
      <div className="fw-medium">{value || "—"}</div>
    </div>
  );

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header border-0 custom-modal-header">
              <div className="page-title">
                <h4>{isEdit ? "Appointment Details" : "Book Appointment"}</h4>
              </div>
              <div className="d-flex align-items-center gap-2">
                {isEdit && (
                  <button className="btn btn-sm btn-success" onClick={handleCreateInvoice} type="button">
                    + Create Invoice
                  </button>
                )}
                <button type="button" className="close" onClick={onClose}><span>×</span></button>
              </div>
            </div>

            <div className="modal-body custom-modal-body">

              {isEdit ? (
                <form onSubmit={handleStatusUpdate}>
                  {/* Header info strip */}
                  <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
                    {editRecord?.tokenNumber && (
                      <span className="badge bg-dark fs-6">Token #{editRecord.tokenNumber}</span>
                    )}
                    {editRecord?.appointmentID && (
                      <span className="text-muted small">Appt ID: {editRecord.appointmentID}</span>
                    )}
                    <span className={`badge bg-${STATUS_BADGE[editRecord?.status] || "primary"} ms-auto`}>
                      {editRecord?.status === "NoShow" ? "No Show" : (editRecord?.status || "Booked")}
                    </span>
                  </div>

                  {/* Read-only appointment details */}
                  <div className="row border rounded p-2 mb-3 mx-0" style={{ background: "#f9fafb" }}>
                    {readVal("Patient", editRecord?.patientName)}
                    {readVal("Doctor", editRecord?.doctorName ? `Dr. ${editRecord.doctorName}` : null)}
                    {readVal("Procedure", editRecord?.procedureName)}
                    {readVal("Date", editRecord?.appointmentDate ? new Date(editRecord.appointmentDate).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric" }) : null)}
                    {readVal("From", fmt12((editRecord?.fromTime || "").split(".")[0]))}
                    {readVal("To", fmt12((editRecord?.toTime || "").split(".")[0]))}
                    {editRecord?.remarks && (
                      <div className="col-12 mt-1">
                        <div className="text-muted small">Remarks</div>
                        <div className="fw-medium">{editRecord.remarks}</div>
                      </div>
                    )}
                  </div>

                  {/* Editable section */}
                  <div className="row">
                    <div className="col-lg-6 mb-3">
                      <label className="form-label">Update Status</label>
                      <Select
                        classNamePrefix="react-select"
                        options={STATUS_OPTIONS}
                        value={STATUS_OPTIONS.find((o) => o.value === form.status) || null}
                        onChange={(opt) => setForm((f) => ({ ...f, status: opt?.value ?? "Booked" }))}
                        menuPosition="fixed"
                        styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                    </div>
                    <div className="col-lg-6 mb-3">
                      <label className="form-label">Remarks</label>
                      <textarea className="form-control" rows={2} value={form.remarks || ""} onChange={set("remarks")} />
                    </div>
                  </div>

                  <div className="modal-footer-btn">
                    <button type="button" className="btn btn-cancel me-2" onClick={onClose}>Close</button>
                    <button type="submit" className="btn btn-submit" disabled={saving}>
                      {saving ? "Saving..." : "Update Status"}
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleBook}>
                  <div className="row">
                    <div className="col-lg-12 mb-3">
                      <label className="form-label">Patient <span className="text-danger">*</span></label>
                      <Select
                        classNamePrefix="react-select"
                        options={patientOptions}
                        onChange={(opt) =>
                          setForm((f) => ({ ...f, patientId: opt?.value ?? "", patientName: opt?.patientName ?? "" }))
                        }
                        value={patientOptions.find((o) => o.value === form.patientId) || null}
                        placeholder="Search patient by name, mobile or OP No..."
                        isClearable
                        isSearchable
                        menuPosition="fixed"
                        styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                    </div>
                    <div className="col-lg-6 mb-3">
                      <label className="form-label">Doctor <span className="text-danger">*</span></label>
                      <Select
                        classNamePrefix="react-select"
                        options={doctorOptions}
                        value={doctorOptions.find((o) => o.value === form.doctorId) || null}
                        onChange={(opt) => setForm((f) => ({ ...f, doctorId: opt?.value ?? "" }))}
                        placeholder="Select Doctor"
                        menuPosition="fixed"
                        styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                      {selectedDoc?.availability && (
                        <small className="text-muted mt-1 d-block">
                          Available: {selectedDoc.availability}
                        </small>
                      )}
                    </div>
                    <div className="col-lg-6 mb-3">
                      <label className="form-label">
                        Procedure
                        {selectedProc && (
                          <span className="text-muted ms-2 small">({selectedProc.duration} min)</span>
                        )}
                      </label>
                      <Select
                        classNamePrefix="react-select"
                        options={procedureOptions}
                        value={procedureOptions.find((o) => o.value === form.procedureId) || null}
                        onChange={handleProcedureChange}
                        placeholder="Select Procedure (sets duration)"
                        isClearable
                        menuPosition="fixed"
                        styles={{ menu: (base) => ({ ...base, zIndex: 9999 }) }}
                      />
                    </div>
                    <div className="col-lg-4 mb-3">
                      <label className="form-label">Date <span className="text-danger">*</span></label>
                      <input type="date" className="form-control" value={form.appointmentDate} onChange={set("appointmentDate")} />
                    </div>
                    <div className="col-lg-4 mb-3">
                      <label className="form-label">From Time <span className="text-danger">*</span></label>
                      <input type="time" className="form-control" value={form.fromTime} onChange={handleFromTimeChange} step="600" />
                    </div>
                    <div className="col-lg-4 mb-3">
                      <label className="form-label">
                        To Time
                        {form.fromTime && form.toTime && (
                          <span className="text-muted ms-2 small">
                            ({(() => {
                              const [fh, fm] = form.fromTime.split(":").map(Number);
                              const [th, tm] = form.toTime.split(":").map(Number);
                              const diff = (th * 60 + tm) - (fh * 60 + fm);
                              return diff > 0 ? `${diff} min` : "";
                            })()})
                          </span>
                        )}
                      </label>
                      <input type="time" className="form-control" value={form.toTime} onChange={set("toTime")} step="600" />
                    </div>
                    <div className="col-lg-12 mb-3">
                      <label className="form-label">Remarks</label>
                      <textarea className="form-control" rows={2} value={form.remarks} onChange={set("remarks")} />
                    </div>
                  </div>
                  <div className="modal-footer-btn">
                    <button type="button" className="btn btn-cancel me-2" onClick={onClose}>Cancel</button>
                    <button type="submit" className="btn btn-submit" disabled={saving}>
                      {saving ? "Saving..." : "Book Appointment"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose} />
    </>
  );
};

export default AppointmentModal;
